<template>
	<div class="bg5 r2 b1 bc7 p4" v-if="customBenefits && customBenefits.length">
		<!-- Build form from customBenefits -->
		<div
			v-for="customBenefit in customBenefits"
			:class="[`mb6`, { 'b2 bcWarning p2 r2': isGodMode }]"
		>
			<div class="xs-12 flex wrap xs-middle">
				<!-- Value -->
				<div class="xs-10">
					<label class="bloxk xs-12 fw7 mb1 lh125" :for="customBenefit.typeId">
						{{ customBenefit.name }}
					</label>
					<j-base-input-number
						:id="customBenefit.typeId"
						v-model="form[customBenefit.typeId]"
						@blur="upsertBenefitOption(customBenefit.typeId)"
					/>
				</div>

				<!-- Currency -->
				<div class="xs-2 pl2 pt4">
					{{ currency }}
				</div>
			</div>

			<!-- ########################################## -->
			<!-- God mode will allow to see data bindings + special buttons for liquid generation -->
			<!-- ########################################## -->
			<template v-if="isGodMode">
				<div class="xs-12 bg7--l5 p4 r2 mt1">
					<div class="lh125 mt2">
						<j-button
							size="xsmall"
							type="bordered"
							@click="generateLiquid(customBenefit.typeId)"
						>
							Get Case Liquid
						</j-button>
					</div>
				</div>
			</template>
		</div>
	</div>
</template>

<script>
import JBaseInputNumber from "@/components/JBaseInputNumber"
import JButton from "@/components/JButton"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

export default {
	components: {
		JBaseInputNumber,
		JButton
	},

	data() {
		return {
			// Form data from generated form from benefits will be saved here
			// Properties of form will be benefit typeIds
			form: {},

			/**
			 * Thse benfits are defined by Elena and we shouldn't display them
			 * because they are being displayed in different component and they have some logic
			 */
			ignoredTypeIds: [
				"product_liability",
				"deductible_north_america",
				"deductible_rest_of_the_world",
				"personal_injury",
				"property_damage",
				"recall_expenses_extension",
				"recall_expenses_extension",
				"manufacturers_errors_and_omissions_extension",
				"manufacturers_errors_and_omissions_extension"
			],

			// Will be fetched from API
			benefits: []
		}
	},

	computed: {
		offer() {
			return this.$store.getters["caseManagement/offer"]
		},

		customBenefits() {
			return this.benefits?.filter(
				benefit => !this.ignoredTypeIds.includes(benefit.typeId)
			)
		},

		// Find currency in store based on case fact
		currency() {
			return (
				this.$store.state.caseManagement.case?.facts?.find(
					fact => fact.type === "currency"
				)?.value?.stringValue || ""
			)
		},

		/**
		 * God mode feature on/off
		 */
		isGodMode() {
			return this.$store.state.caseManagement.isGodMode
		}
	},

	methods: {
		async upsertBenefitOption(typeId) {
			const query = gql`
				mutation upsertBenefitOptionOfProposalOffer(
					$caseId: String!
					$proposalId: String!
					$offerId: String!
					$input: benefitOptionInput!
				) {
					upsertBenefitOptionOfProposalOffer(
						caseId: $caseId
						offerId: $offerId
						proposalId: $proposalId
						input: $input
					) {
						status
						errors
					}
				}
			`

			const { caseId, proposalId, offerId } = this.$store.state.caseManagement
			const res = await api({
				query,
				variables: {
					caseId,
					proposalId,
					offerId,
					input: {
						typeId,
						value: {
							numberValue: this.form[typeId] || 0
						}
					}
				}
			})

			// Errors
			if (res?.data?.upsertBenefitOptionOfProposalOffer?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.upsertBenefitOptionOfProposalOffer?.errors ||
						this.$t("errors.general")
				)
				this.populateFormFromCache() // revert optimistic UI
				return Promise.resolve()
			}

			return Promise.resolve()
		},

		async fetchBenefits() {
			const query = gql`
				query benefits($where: productWhereInput) {
					products_2(where: $where) {
						list {
							benefits {
								name
								typeId
								value
							}
						}
					}
				}
			`

			// Get currently selected offer's productId
			const productId = this.$store.getters["caseManagement/offer"]?.product
				?.productId

			// API call
			const res = await api({ query, variables: { where: { productId } } })
			this.benefits = res?.data?.products_2?.list?.[0]?.benefits || []
		},

		async refetchStore() {
			const caseCurrencyCode = this.$store.state.caseManagement.case?.facts?.find(
				fact => fact.type === "premiumCurrency"
			)?.value?.stringValue

			await this.$store.dispatch("caseManagement/fetchProposals", {
				caseId: this.$store.state.caseManagement.caseId,
				options: { currencyCodes: [caseCurrencyCode] }
			})

			return Promise.resolve()
		},

		populateFormFromCache() {
			const benefitOptions = this.offer?.benefitOptions || []

			const newForm = {}
			benefitOptions.forEach(
				benefitOption =>
					(newForm[benefitOption.benefitTypeId] =
						benefitOption?.value?.numberValue)
			)

			this.form = { ...this.form, ...newForm }
		},

		generateLiquid(typeId) {
			// Helper to save value to clipboard
			//  Not guaranteed to work everywhere, works in Chrome
			const copyToClipboard = str => {
				const el = document.createElement("textarea")
				el.value = str
				el.setAttribute("readonly", "")
				el.style.position = "absolute"
				el.style.left = "-9999px"
				document.body.appendChild(el)
				el.select()
				document.execCommand("copy")
				document.body.removeChild(el)
			}

			// Generate liquid
			const generatedLiquid = `{%- assign benefitOption = data.cases.list[0].proposals[0].basket[0].benefitOptions | where:"benefitTypeId","${typeId}" -%} {{- benefitOption[0].value.numberValue -}}`
			console.log(generatedLiquid)

			// Save result to clipboard
			copyToClipboard(generatedLiquid)

			// Show message
			this.$store.dispatch("addMessage", {
				delay: 1000,
				message: {
					type: "neutral",
					text: "Copied to your clipboard!"
				}
			})
		}
	},

	mounted() {
		this.fetchBenefits()
	},

	watch: {
		// Anytime offer updates, repopulate this.form with new values from offer.benefitOptions
		offer: {
			handler() {
				this.populateFormFromCache()
			},
			immediate: true,
			deep: true
		}
	}
}
</script>
